import React, { useCallback, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  Chat,
  Channel,
  ChannelList,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
  InfiniteScroll,
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";

import {
  Box,
  Divider,
  CircularProgress,
  ThemeProvider,
  Typography,
  Paper,
  GlobalStyles,
  Button,
} from "@mui/material";

import CustomChannelPreview from "./CustomChannelPreview";
import CustomChannelHeader from "./CustomChannelHeader";
import CustomMessageList from "./CustomMessageList";
import darkTheme from "../dashboardTheme";

// Stream's dark theme is cool grey; map its variables onto the dashboard's espresso and gold.
const STREAM_THEME = {
  "& .str-chat": {
    "--str-chat__font-family": '"DM Sans", "Segoe UI", sans-serif',
    "--str-chat__primary-color": "#c7a45d",
    "--str-chat__active-primary-color": "#aa8238",
    "--str-chat__on-primary-color": "#15120f",
    "--str-chat__background-color": "#252018",
    "--str-chat__secondary-background-color": "#1d1915",
    "--str-chat__surface-color": "#2e281f",
    "--str-chat__secondary-surface-color": "#2e281f",
    "--str-chat__primary-surface-color": "rgba(199, 164, 93, 0.16)",
    "--str-chat__primary-surface-color-low-emphasis": "rgba(199, 164, 93, 0.08)",
    "--str-chat__text-color": "#f5efe3",
    "--str-chat__text-low-emphasis-color": "rgba(245, 239, 227, 0.55)",
    "--str-chat__disabled-color": "rgba(245, 239, 227, 0.3)",
  },
};

/** Shown when the channel query fails, instead of Stream's default of rendering nothing. */
function ChannelListError({ error, onRetry }) {
  return (
    <Box sx={{ p: 3, textAlign: "center" }} role="alert">
      <Typography sx={{ mb: 0.5, fontWeight: 600 }}>Couldn't load chats</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {error?.message || "The chat service didn't respond."}
      </Typography>
      <Button variant="outlined" size="small" onClick={onRetry}>
        Try again
      </Button>
    </Box>
  );
}

const ChatComponent = () => {
  const { currentUser, chatClient, chatReady } = useAuth();
  // Bumping the key remounts ChannelList, which re-runs its channel query.
  const [listKey, setListKey] = useState(0);
  const ListError = useCallback(
    ({ error }) => <ChannelListError error={error} onRetry={() => setListKey((key) => key + 1)} />,
    []
  );

  const filters = { members: { $in: ["lamdahcom"] } };
  const options = { presence: true, state: true };
  const sort = { last_message_at: -1 };

  // Querying channels before the socket connects fails, so wait for it.
  if (!currentUser || !chatClient || !chatReady) {
    return (
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={{
            height: "calc(100vh - 64px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "background.default",
            color: "text.primary",
          }}
        >
          <CircularProgress color="primary" size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading chat...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <GlobalStyles />
      <Box
        sx={{
          height: "91.2vh",
          width: "100%",
          bgcolor: "background.default",
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={6}
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            maxWidth: "96vw",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 0 12px rgba(0,0,0,0.4)",
            ...STREAM_THEME,
          }}
        >
          <Chat client={chatClient} theme="str-chat__theme-dark">
            {/* Sidebar */}
            <Box
              sx={{
                width: "30%",
                borderRight: "1px solid",
                borderColor: "divider",
                bgcolor: "#1d1915",
              }}
            >
              <Box sx={{ pl: 2, pt: 2, pb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    color: "text.primary",
                  }}
                >
                  Chats
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1 }} />
              <ChannelList
                key={listKey}
                LoadingErrorIndicator={ListError}
                Preview={CustomChannelPreview}
                filters={filters}
                sort={sort}
                options={options}
                showChannelSearch
                Paginator={InfiniteScroll}
              />
            </Box>

            {/* Chat Window */}
            <Box sx={{ flexGrow: 1, minWidth: 0, bgcolor: "background.paper" }}>
              <Channel>
                <Window>
                  <CustomChannelHeader />
                  <CustomMessageList />
                  <MessageInput focus />
                </Window>
                <Thread />
              </Channel>
            </Box>
          </Chat>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default ChatComponent;
